import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import useBodyScrollLock from "@/hooks/useBodyScrollLock";
import { toast } from "sonner";
import { X } from "lucide-react";
import CertForm from "@/components/cert/CertForm";
import CertCard from "@/components/cert/CertCard";

export function trafficStatus(exp) {
  if (!exp) return "unknown";
  const now = new Date();
  const diff = (new Date(exp) - now) / 86400000;
  if (diff < 0) return "expired";
  if (diff <= 30) return "expiring_soon";
  return "current";
}

export function ModalShell({ title, onClose, children, maxWidth = "max-w-xl" }) {
  useBodyScrollLock(true);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end md:items-center justify-center p-0 md:p-6"
      onClick={onClose}>
      <div className={`bg-background brutal-card w-full md:${maxWidth} max-h-[92vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b-2 border-foreground dark:border-safety-yellow p-4 md:p-5 sticky top-0 bg-background">
          <div className="font-heading text-2xl">{title}</div>
          <button onClick={onClose} data-testid="modal-close"
            className="h-12 w-12 border-2 border-foreground dark:border-safety-yellow hover:bg-safety-yellow hover:text-black">
            <X className="w-5 h-5 mx-auto" strokeWidth={3} />
          </button>
        </div>
        <div className="p-5 md:p-6">{children}</div>
      </div>
    </div>
  );
}

function CertPhotoViewer({ cert, onClose }) {
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    let url = null;
    if (cert.photo_path) {
      api.get("/files", { params: { path: cert.photo_path }, responseType: "blob" })
        .then((r) => {
          if (cancelled) return;
          url = URL.createObjectURL(r.data);
          setBlobUrl(url);
        })
        .catch(() => toast.error("Could not load image"));
    }
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [cert.photo_path]);
  return (
    <div className="fixed inset-0 z-[90] bg-black/90 flex items-center justify-center p-4" onClick={onClose}>
      <button onClick={onClose} data-testid="cert-viewer-close"
        className="absolute top-4 right-4 h-12 w-12 bg-safety-yellow text-black border-2 border-black inline-flex items-center justify-center z-10">
        <X className="w-6 h-6" strokeWidth={3} />
      </button>
      <div className="max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="brutal-card bg-background p-5">
          <div className="font-heading text-2xl">{cert.cert_type}</div>
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mt-1">
            Expires {cert.expiration_date}
          </div>
          <div className="mt-4 border-2 border-foreground dark:border-safety-yellow bg-black flex items-center justify-center min-h-[280px]">
            {blobUrl
              ? <img src={blobUrl} alt={cert.cert_type} className="max-h-[60vh] w-auto object-contain" />
              : <div className="text-muted-foreground py-12 font-bold uppercase tracking-wider">Loading...</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

const INITIAL_FORM = { cert_type: "", expiration_date: "", issuer: "", custom_name: "", photo: null };

async function uploadCertPhoto(certId, file) {
  const fd = new FormData();
  fd.append("file", file);
  await api.post(`/certifications/${certId}/photo`, fd,
    { headers: { "Content-Type": "multipart/form-data" } });
}

export default function CertModal({ employee, certTypes, userRole, onClose, onRefresh }) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const isAdmin = userRole === "admin";

  const resetForm = () => { setForm(INITIAL_FORM); setEditing(null); setExpanded(false); };

  const submit = async (e) => {
    e.preventDefault();
    const isCustom = form.cert_type === "__custom__";
    const cert_type = isCustom ? form.custom_name.trim() : form.cert_type;
    if (!cert_type) { toast.error("Title is required"); return; }
    if (!form.expiration_date) { toast.error("Expiration date is required"); return; }

    setSaving(true);
    try {
      const payload = {
        cert_type,
        expiration_date: form.expiration_date,
        issuer: form.issuer || null,
      };
      let certId;
      if (editing) {
        await api.patch(`/certifications/${editing}`, payload);
        certId = editing;
        toast.success("Certification updated");
      } else {
        const { data } = await api.post("/certifications", { ...payload, employee_id: employee.id });
        certId = data.id;
        toast.success("Certification added");
      }
      if (form.photo) await uploadCertPhoto(certId, form.photo);
      resetForm();
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (c) => {
    const isStandardType = certTypes.includes(c.cert_type);
    setForm({
      cert_type: isStandardType ? c.cert_type : "__custom__",
      custom_name: isStandardType ? "" : c.cert_type,
      expiration_date: c.expiration_date || "",
      issuer: c.issuer || "",
      photo: null,
    });
    setEditing(c.id);
    setExpanded(true);
  };

  const disableCert = async (c) => {
    if (!window.confirm(`Disable "${c.cert_type}"? It will be archived.`)) return;
    try {
      await api.patch(`/certifications/${c.id}`, { disabled: true });
      toast.success("Certification disabled");
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const uploadPhoto = async (c, file) => {
    try {
      await uploadCertPhoto(c.id, file);
      toast.success("Photo uploaded");
      onRefresh && onRefresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggleForm = () => { if (expanded) resetForm(); else setExpanded(true); };

  return (
    <ModalShell title={`Certs · ${employee.name}`} onClose={onClose}>
      <CertForm
        form={form}
        setForm={setForm}
        certTypes={certTypes}
        expanded={expanded}
        editing={editing}
        saving={saving}
        onToggle={toggleForm}
        onCancelEdit={resetForm}
        onSubmit={submit}
      />

      <div className="space-y-3">
        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Existing</div>
        {employee.certifications?.length ? employee.certifications.map((c) => (
          <CertCard
            key={c.id}
            cert={c}
            isAdmin={isAdmin}
            onView={setViewing}
            onUpload={uploadPhoto}
            onEdit={startEdit}
            onDisable={disableCert}
          />
        )) : <div className="text-sm text-muted-foreground">No certifications yet.</div>}
      </div>

      {viewing && <CertPhotoViewer cert={viewing} onClose={() => setViewing(null)} />}
    </ModalShell>
  );
}
